import { Component, Host, h, Prop, State, Watch } from '@stencil/core';
import { Listen } from '@stencil/core/internal';
import { store } from '~/store';
import { container } from '@rdss/client';
import { UserService } from '~/data-access';
import favorites from './assets/favorites.svg'
import fwf from './assets/fwf.svg'
import orders from './assets/orders.svg'
import recommendations from './assets/recommendations.svg'
import coin from './assets/coin.svg'

@Component({
    tag: 'wf-account-menu',
    styleUrl: 'wf-account-menu.scss',
    shadow: true,
})
// eslint-disable-next-line require-jsdoc
export class WfAccountMenu {
    /**
     * The 'formAction' prop is the URL where the login form will be submitted.
     */
    @Prop() formAction = '/account/login/sTarget/account/sTargetAction/index'

    /**
     * The 'snippets' prop contains various labels used in the component.
     */
    @Prop() snippets = {
        loginButtonLabel: 'Anmelden',
        accountButtonLabel: 'Zu meinem Konto',
        logoutLabel: 'Abmelden',
        emailPlaceholder: 'E-Mail-Adresse',
        passwordPlaceholder: 'Passwort',
        loginErrorMessage: 'Deine Zugangsdaten konnten keinem Benutzer zugeordnet werden',
        formAriaLabel: 'Login-Formular',
        listItems: {
            favorites: 'Meine Favoriten',
            gotofavorites: 'Zu meinen Favoriten',
            orders: 'Meine Bestellungen',
            gotoorders: 'Zu meinen Bestellungen',
            recommendations: 'Meine Empfehlungen',
            gotorecommendations: 'Zu meinen Empfehlungen',
            loyalty: 'Weinfreunde+',
            referFriends: 'Freunde werben',
        },
        account: 'Zu meinem Konto',
        logout: 'Abmelden',
        registerAccount: 'Jetzt Kundenkonto anlegen',
        forgotPassword: 'Passwort vergessen?',
        done: 'Alles erledigt?',
        new: 'Neu hier?',
    }

    /**
     * The 'formAutocomplete' prop controls whether the browser should enable autocomplete for the form.
     * @type {boolean}
     */
    @Prop() formAutocomplete: boolean

    /**
     * The 'enableLoyalty' prop controls whether the loyalty feature is enabled.
     */
    @Prop() enableLoyalty = false

    /**
     * The 'isUserLoggedIn' state indicates whether the user is logged in or not.
     */
    @Prop() isUserLoggedIn: boolean

    /**
     * The 'userService' prop is an instance of the `UserService` class.
     */
    @Prop() readonly userService = container.resolve(UserService)

    /**
     * The 'csrfToken' state contains the CSRF token.
     */
    @State() private csrfToken: string

    /**
     * The 'isLoading' state indicates whether the login form is being submitted.
     */
    @State() private isLoading: boolean

    /**
     * The 'hasError' state indicates whether there is an error in the login form.
     */
    @State() private hasError: boolean

    private formElement: HTMLFormElement

    /**
     * It checks if the user is logged in and gets the CSRF token.
     */
    @Watch('isUserLoggedIn')
    componentWillLoad() {
        this.hasError = false
        this.setCsrfToken()
    }

    /**
     * This method handles the 'loginError' event.
     * When the 'loginError' event is dispatched, it sets the 'hasError' state to true.
     */
    @Listen('loginError', { target: 'document' })
    handleLoginError() {
        this.hasError = true
    }

    /**
     * This method handles 'keyup' events on the component.
     * If the 'Enter' key is pressed, it submits the form.
     */
    @Listen('keyup')
    async handleEnter({ key }: KeyboardEvent) {
        this.hasError = false
        if (key === 'Enter') await this.handleSubmit()
    }

    /**
     * The 'render' method returns the JSX for the account menu.
     * It includes a form for login and displays user information if the user is logged in.
     */
    render({ wishlist } = store.state) {
        return (
            <Host>
                <div class={{ 'user-logged-in': this.isUserLoggedIn, wrapper: true }}>
                    <ul role="menu">
                        <li role="presentation"
                            class="icon-link">
                            <a role="menuitem" href="/note" class="note-item" aria-label={this.snippets.listItems.gotofavorites}>
                                <img src={favorites} alt="Favoriten Icon"/>
                                <span>{this.snippets.listItems.favorites}</span>
                                <vx-counter-badge style={{ opacity: wishlist.length > 0 ? '1' : '0' }}
                                    quantity={wishlist.length}/>
                            </a>
                        </li>
                        {this.isUserLoggedIn && (
                            <li role="presentation" class="icon-link">
                                <a role="menuitem" aria-label={this.snippets.listItems.gotoorders} href="/account/orders">
                                    <img src={orders} alt="Bestellungen Icon"/>
                                    <span>{this.snippets.listItems.orders}</span>
                                </a>
                            </li>
                        )}
                        {this.isUserLoggedIn && (
                            <li role="presentation" class="icon-link">
                                <a role="menuitem"
                                    aria-label={this.snippets.listItems.gotorecommendations}
                                    href="/meine-empfehlungen">
                                    <img src={recommendations} alt="Empfehlungen Icon"/>
                                    <span>{this.snippets.listItems.recommendations}</span>
                                </a>
                            </li>
                        )}
                        {(this.enableLoyalty && this.isUserLoggedIn) && (
                            <li role="presentation" class="icon-link">
                                <a role="menuitem" aria-label={this.snippets.listItems.loyalty} href="/account?view=loyalty">
                                    <img src={coin} alt="Weinfreunde+ Loyalty Logo"/>
                                    <span>{this.snippets.listItems.loyalty}</span>
                                </a>
                            </li>
                        )}
                        {this.isUserLoggedIn && (
                            <li role="presentation" class="icon-link">
                                <a role="menuitem" href="/freunde-werben" aria-label={this.snippets.listItems.referFriends}>
                                    <img src={fwf} alt="Freunde werben Freunde Icon"/>
                                    <span>{this.snippets.listItems.referFriends}</span>
                                </a>
                            </li>
                        )}
                        {this.isUserLoggedIn && (
                            <li role="presentation">
                                <a role="menuitem" href="/account" aria-label={this.snippets.accountButtonLabel}>
                                    <vx-button
                                        label={this.snippets.accountButtonLabel}
                                        height="40px"
                                        fullwidth raised/>
                                </a>

                                <div class="pt-2 text-normal">
                                    <span class="mr-1">{this.snippets.done}</span>
                                    <a role="menuitem" onClick={this.handleLogoutClick}
                                        aria-label={this.snippets.logoutLabel}
                                        class="underline text-black"
                                        href="/account/logout">{this.snippets.logoutLabel}</a>
                                </div>
                            </li>
                        )}
                        {!this.isUserLoggedIn && (
                            <li role="presentation">
                                {this.renderLoginForm()}

                                <div class="pt-2 text-normal">
                                    <span class="text-accent mr-1">{this.snippets.new}</span>
                                    <a role="menuitem" href="/account?registerMode=registerCustomer" class="underline text-black">
                                        {this.snippets.registerAccount}
                                    </a>
                                </div>
                            </li>
                        )}
                    </ul>
                </div>
            </Host>
        )
    }

    /**
     * The 'renderLoginForm' method returns the JSX for the login form.
     */
    private renderLoginForm() {
        return <form
            autocomplete={this.formAutocomplete ? 'on' : 'off'}
            class={{ 'is--loading': this.isLoading, 'form-container': true }}
            action={this.formAction}
            method="post"
            onSubmit={this.handleSubmit}
            ref={el => (this.formElement = el)}
            part="form"
            aria-label={this.snippets.formAriaLabel}>

            <div class="loading-overlay">
                <vx-circular-progress show={this.isLoading}/>
            </div>

            <span>Mein Konto</span>

            <small class="error-message" role="alert">
                {this.hasError && (
                    <span>{this.snippets.loginErrorMessage}</span>
                )}
            </small>

            <input autocomplete={this.formAutocomplete ? 'username' : 'off'}
                onInput={this.validateInput}
                pattern="^\S+@\S+\.\S+$"
                class={{ 'has-error': this.hasError }}
                disabled={this.isLoading}
                placeholder={this.snippets.emailPlaceholder}
                required
                name="email"
                type="email"
                id="email"
                aria-invalid={this.hasError ? 'true' : 'false'}
                aria-required="true"/>

            <input autocomplete={this.formAutocomplete ? 'current-password' : 'off'}
                onInput={this.validateInput}
                pattern=".{4,}"
                class={{ 'has-error': this.hasError }}
                disabled={this.isLoading}
                placeholder={this.snippets.passwordPlaceholder}
                required
                name="password"
                type="password"
                id="passwort"
                aria-invalid={this.hasError ? 'true' : 'false'}
                aria-required="true"/>

            <input type="hidden" name="__csrf_token" value={this.csrfToken}/>

            <a href="/account/password" title="Passwort vergessen?"
                class="text-slate underline text-normal">
                Passwort vergessen?
            </a>

            <vx-button
                disabled={this.hasError || this.isLoading}
                fullwidth
                raised
                height="40px"
                type="submit"
                label={this.snippets.loginButtonLabel}/>
        </form>
    }

    /**
     * This method validates the input fields of the login form.
     * It takes an event of type `UIEvent` as a parameter.
     *
     * @param {UIEvent} e - The event triggered when a user types into an input field.
     */
    private validateInput(e: UIEvent) {
        const input = e.target as HTMLInputElement
        if (!input.value.match(input.pattern)) return input.classList.add('has-error')

        input.classList.remove('has-error')
    }

    /**
     * This method sets the CSRF token to the state.
     */
    private setCsrfToken() {
        this.csrfToken = window.CSRF?.getToken()
    }

    /**
     * The 'handleSubmit' method submits the login form.
     */
    private handleSubmit = async (e?: UIEvent) => {
        e?.preventDefault()
        this.isLoading = true
        await this.userService.formLogin(this.formElement)
        this.isLoading = false
    }

    /**
     * This method handles the logout click event.
     */
    private handleLogoutClick = async (e: UIEvent) => {
        e.preventDefault()
        await this.userService.logout()
    }
}
